import { Injectable } from '@angular/core';
import {
  HttpClient as HttpClientAngular,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { filter, lastValueFrom, map, Observable, tap, throwError, timeout } from 'rxjs';
import { HttpClient } from 'src/core/common/domain/http/http-client';
import { SessionService } from 'src/core/session/domain/session.service';
import { ApiService } from 'src/core/api/domain/api.service';
import { SystemService } from 'src/core/system/system.service';
import { EventsService } from 'src/core/events/events.service';
import { AppIsOutdatedEvent } from 'src/core/events/app-is-outdated.event';
import { NewAppVersionAvailableEvent } from 'src/core/events/new-app-version-available.event';
import { RequestTimeouts } from 'src/core/common/infrastructure/http/request-timeouts';

@Injectable({
  providedIn: 'any',
})
export class AngularHttpClient implements HttpClient {
  constructor(
    private readonly http: HttpClientAngular,
    private readonly session: SessionService,
    private readonly apiService: ApiService,
    private readonly eventsService: EventsService
  ) {}

  async get<ResponseType>(
    url: string,
    params: Record<string, unknown> = {},
    headers?: { [name: string]: string | string[] },
    requestIsBasicAuthenticated: boolean = false,
    requestIsAuthenticated: boolean = false
  ): Promise<ResponseType> {
    const events = this.http.get<ResponseType>(url, {
      params: {
        ...(params as unknown as HttpParams),
      },
      headers: await this.getHttpHeaders(headers, requestIsBasicAuthenticated, requestIsAuthenticated),
      observe: 'events',
      reportProgress: true,
    });

    return lastValueFrom(this.responseBody(events, url));
  }

  async post<ResponseType = void>(
    url: string,
    data: unknown,
    headers?: { [name: string]: string | string[] },
    requestIsBasicAuthenticated: boolean = false,
    requestIsAuthenticated: boolean = false
  ): Promise<ResponseType> {
    const events = this.http.post<ResponseType>(url, data, {
      headers: await this.getHttpHeaders(headers, requestIsBasicAuthenticated, requestIsAuthenticated),
      observe: 'events',
      reportProgress: true,
    });

    return lastValueFrom(this.responseBody(events, url));
  }

  /**
   * Progress events are observed only to tell a slow request, which keeps moving, from a stalled one. A stalled
   * request is aborted and fails like one that never got a response (status 0), so every caller keeps reacting
   * to it the way that has been agreed with the client.
   */
  private responseBody<ResponseType>(
    events: Observable<HttpEvent<ResponseType>>,
    url: string
  ): Observable<ResponseType> {
    const endpoint = RequestTimeouts.endpointOf(url);

    return events.pipe(
      timeout({
        each: RequestTimeouts.stallTimeoutFor(endpoint),
        with: () =>
          throwError(
            () => new HttpErrorResponse({ status: 0, statusText: RequestTimeouts.STALLED_STATUS_TEXT, url: endpoint })
          ),
      }),
      filter((event): event is HttpResponse<ResponseType> => event.type === HttpEventType.Response),
      map((response) => response.body),
      tap((response: any) => {
        const code = response?.action?.code ?? null;
        if (code === 'APP_IS_OUTDATED') {
          this.eventsService.publishAppIsOutdated(new AppIsOutdatedEvent());
        }
        if (code === 'NEW_APP_VERSION_AVAILABLE') {
          this.eventsService.publishNewAppVersionIsAvailable(new NewAppVersionAvailableEvent());
        }
      })
    );
  }

  private async getHttpHeaders(
    headers?: { [name: string]: string | string[] },
    requestIsBasicAuthenticated: boolean = false,
    requestIsAuthenticated: boolean = false
  ): Promise<HttpHeaders> {
    let httpHeaders = new HttpHeaders(headers);

    // otherwise the API will fail
    httpHeaders = httpHeaders.set('Accept', 'application/json');

    if (requestIsBasicAuthenticated) {
      httpHeaders = this.addCommonHeaders(httpHeaders);
      httpHeaders = this.addBasicAuthorizationHeader(httpHeaders);
    }

    if (requestIsAuthenticated) {
      httpHeaders = this.addAuthorizationHeader(httpHeaders);
    }

    return httpHeaders;
  }

  private addBasicAuthorizationHeader(headers: HttpHeaders): HttpHeaders {
    const accessToken = this.session.getAccessToken();
    headers = headers.set('Authorization', `Bearer ${accessToken.value}`);
    headers = headers.set('usernameToken', this.apiService.getUsernameTokenHeader());
    headers = headers.set('passwordToken', this.apiService.getPasswordTokenHeader());

    return headers;
  }

  private addCommonHeaders(headers: HttpHeaders): HttpHeaders {
    const currentLanguage = this.session.getCurrentLanguage();
    headers = headers.set('X-Language', currentLanguage);
    headers = headers.set('X-App-Version', SystemService.APP_VERSION);

    return headers;
  }

  private addAuthorizationHeader(headers: HttpHeaders): HttpHeaders {
    const authToken = this.session.getAuthToken();
    headers = headers.set('X-Auth-Token', authToken.value);

    return headers;
  }
}
